import type { Language } from '../../../src/api/langConfig';
import { CHECKOUT_FULFILLMENT_DEADLINE_GRACE_MS } from '../api/constants';
import { getDayAndMonth, getOrderDeadlineClock, orderDateDays, orderMenuDate, toDateFromServer, toDayString } from '../api/dates';
import type { Meal, MealLine, ModelComplexity, Order } from '../types';
import { isMealAvailableForDate } from './mealAvailability';
import { isCommittedOrder, orderHasContentLines } from './orders';
import { findSetDayMenuRangeForDate } from './setDayMenuRanges';

export type OrderDayDeadlineContext = {
    modelConstants: ModelComplexity | null | undefined;
    setDayMenuRangesRoot: Record<string, unknown> | null | undefined;
    dailyMeals: Record<string, Meal> | null | undefined;
    /** Menu catalog meals (`MEALS_PATH`) keyed by meal key — for set-menu (`courseType: main`) checks. */
    menuMealsCatalog: Record<string, Meal> | null | undefined;
    /** That day's menu snapshot meals keyed by catalog meal key — same source as order pricing (`menuPath/.../date`). */
    menuMealsForOrderDate?: Record<string, Meal> | null | undefined;
};

/** Hour 0–23 for set-menu / menu-meal deadlines; falls back to {@link ModelComplexity.orderTime}. */
export function orderMenuTimeFromModel(modelConstants: ModelComplexity | null | undefined): number {
    const menuTime = modelConstants?.orderMenuTime;
    if (typeof menuTime === 'number' && Number.isFinite(menuTime)) {
        return menuTime;
    }
    return modelConstants?.orderTime ?? 22;
}

export function orderDayDeadlineContextFromPricing(
    modelConstants: ModelComplexity | null | undefined,
    pricing: {
        dailyMeals: Record<string, Meal> | null | undefined;
        meals: Record<string, Meal> | null | undefined;
        setDayMenuRangesRoot: Record<string, unknown> | null | undefined;
    },
): OrderDayDeadlineContext {
    return {
        modelConstants,
        setDayMenuRangesRoot: pricing.setDayMenuRangesRoot,
        dailyMeals: pricing.dailyMeals,
        menuMealsCatalog: pricing.meals,
    };
}

/** Earliest calendar day still open for cancel/change (server clock + school `orderTime` / `orderDays`). */
export function getGeneralOrderDeadlineDay(modelConstants: ModelComplexity | null | undefined, at: Date = new Date()): string {
    return toDayString(orderDateDays(modelConstants?.orderTime ?? 22, modelConstants?.orderDays ?? 1, at));
}

export function isGeneralOrderDeadlinePassed(orderDate: string, modelConstants: ModelComplexity | null | undefined, at: Date = new Date()): boolean {
    return orderDate < getGeneralOrderDeadlineDay(modelConstants, at);
}

/** Civil "today" in Europe/Athens as `DATE_FORMAT` — floor for order dates regardless of device clock. */
export function getAthensTodayDayString(at: Date = new Date()): string {
    return toDayString(getOrderDeadlineClock(at).calendarDate);
}

export function isOrderDateBeforeAthensToday(orderDate: string, at: Date = new Date()): boolean {
    return orderDate < getAthensTodayDayString(at);
}

export function canOrderMenuMealsForDay(orderDate: string, modelConstants: ModelComplexity | null | undefined, at: Date = new Date()): boolean {
    const mc = modelConstants;
    return toDateFromServer(orderDate) >= orderMenuDate(
        orderMenuTimeFromModel(mc),
        mc?.orderMenuDays ?? 1,
        mc?.orderMenuSpecificDay ?? -1,
        at,
    );
}

export function isSetDayMenuDate(orderDate: string, setDayMenuRangesRoot: Record<string, unknown> | null | undefined): boolean {
    return !!findSetDayMenuRangeForDate(setDayMenuRangesRoot, orderDate);
}

export function hasDailyMealsAvailableForDay(
    orderDate: string,
    dailyMeals: Record<string, Meal> | null | undefined,
    isSetDayMenu: boolean,
): boolean {
    if (isSetDayMenu) {
        return true;
    }
    if (!dailyMeals) {
        return false;
    }
    return Object.entries(dailyMeals).some(([key, meal]) => {
        if ((meal.order ?? -1) < 0) {
            return false;
        }
        return isMealAvailableForDate(meal, orderDate);
    });
}

/** Matches `Order.tsx` / `DaySelector` `cannotOrder` for new orders (not `isChangingOrder`). */
export function isNewOrderBlockedForDay(orderDate: string, ctx: OrderDayDeadlineContext, at: Date = new Date()): boolean {
    if (isOrderDateBeforeAthensToday(orderDate, at)) {
        return true;
    }
    const isPast = isGeneralOrderDeadlinePassed(orderDate, ctx.modelConstants, at);
    const canOrderMealMenu = canOrderMenuMealsForDay(orderDate, ctx.modelConstants, at);
    const isSetDayMenu = isSetDayMenuDate(orderDate, ctx.setDayMenuRangesRoot);
    const hasDailyMeals = hasDailyMealsAvailableForDay(orderDate, ctx.dailyMeals, isSetDayMenu);
    if (isSetDayMenu) {
        return isPast || !canOrderMealMenu;
    }
    return isPast || !(hasDailyMeals || canOrderMealMenu);
}

/** Matches order UI when editing an existing order — only the general deadline applies. */
export function isDayOrderingUiBlocked(orderDate: string, ctx: OrderDayDeadlineContext, isChangingOrder: boolean, at: Date = new Date()): boolean {
    if (isChangingOrder) {
        return isGeneralOrderDeadlinePassed(orderDate, ctx.modelConstants, at);
    }
    return isNewOrderBlockedForDay(orderDate, ctx, at);
}

export function isCancelOrChangeBlockedForDay(orderDate: string, modelConstants: ModelComplexity | null | undefined, at: Date = new Date()): boolean {
    return isGeneralOrderDeadlinePassed(orderDate, modelConstants, at);
}

/** Set-menu / menu-meal line — prefer the published menu row for `courseType` (matches MealsPopup). */
export function isSetMenuMealLineForMenuDeadline(
    line: MealLine,
    menuMealsCatalog: Record<string, Meal> | null | undefined,
    menuMealsForOrderDate?: Record<string, Meal> | null | undefined,
): boolean {
    if (line.isPackage) {
        return false;
    }
    const meal = menuMealsForOrderDate?.[line.key] ?? menuMealsCatalog?.[line.key];
    return meal?.courseType === 'main';
}

export function getOrderSetMenuMealLinesByLineId(
    order: Order | null | undefined,
    menuMealsCatalog: Record<string, Meal> | null | undefined,
    menuMealsForOrderDate?: Record<string, Meal> | null | undefined,
): { lineId: string; line: MealLine }[] {
    if (!order?.meals) {
        return [];
    }
    return Object.entries(order.meals)
        .filter(([, line]) => isSetMenuMealLineForMenuDeadline(line, menuMealsCatalog, menuMealsForOrderDate))
        .map(([lineId, line]) => ({ lineId, line }));
}

/** Draft copy with set-menu / catalog-main meal lines removed (daily meals and snacks unchanged). */
export function draftOrderWithoutSetMenuMealLines(
    draftOrder: Order,
    ctx: OrderDayDeadlineContext,
): Order {
    const setMenuLineIds = getOrderSetMenuMealLinesByLineId(draftOrder, ctx.menuMealsCatalog, ctx.menuMealsForOrderDate).map(({ lineId }) => lineId);
    if (!setMenuLineIds.length || !draftOrder.meals) {
        return draftOrder;
    }
    const meals = { ...draftOrder.meals };
    for (const lineId of setMenuLineIds) {
        delete meals[lineId];
    }
    return { ...draftOrder, meals };
}

/**
 * Checkout fails because of set-menu meals after the menu deadline, but the draft would still
 * have other lines (e.g. daily meals) if those menu meals were removed.
 */
export function isCheckoutBlockedOnlyBySetMenuMealsInDraft(
    orderDate: string,
    draftOrder: Order,
    placedOrder: Order | null | undefined,
    ctx: OrderDayDeadlineContext,
    userLang: Language,
    at: Date = new Date(),
): boolean {
    const blocked = getCheckoutDayAllowedResult(orderDate, draftOrder, placedOrder, ctx, userLang, at);
    if (blocked.ok === false && blocked.category === 'set_menu_deadline') {
        return orderHasContentLines(draftOrderWithoutSetMenuMealLines(draftOrder, ctx));
    }
    return false;
}

/** Matches `Order.tsx` `onCompleteOrder` set-menu validation for completions (not cancellations). */
export function validateSetMenuOrderCompletion(
    orderDate: string,
    draftOrder: Order,
    placedOrder: Order | null | undefined,
    ctx: OrderDayDeadlineContext,
    at: Date = new Date(),
): { ok: true } | { ok: false } {
    if (canOrderMenuMealsForDay(orderDate, ctx.modelConstants, at)) {
        return { ok: true };
    }
    const menuLines = getOrderSetMenuMealLinesByLineId(draftOrder, ctx.menuMealsCatalog, ctx.menuMealsForOrderDate);
    if (menuLines.length === 0) {
        return { ok: true };
    }
    if (!isCommittedOrder(placedOrder)) {
        return { ok: false };
    }
    const placedMenuLines = getOrderSetMenuMealLinesByLineId(placedOrder, ctx.menuMealsCatalog, ctx.menuMealsForOrderDate);
    for (const { lineId, line } of menuLines) {
        const placedLine = placedMenuLines.find(p => p.lineId === lineId)?.line;
        if (!placedLine || line.quantity > placedLine.quantity) {
            return { ok: false };
        }
    }
    return { ok: true };
}

export function orderDeadlinePassedMessage(userLang: Language): string {
    return userLang === 'el'
        ? 'Δεν μπορείς πλέον να κάνεις παραγγελίες, αλλαγές ή ακυρώσεις μετά την προθεσμία.'
        : 'You can no longer place, change, or cancel orders after the deadline.';
}

export function setMenuOrderDeadlineMessage(userLang: Language): string {
    return userLang === 'el'
        ? 'Για αυτή την ημερομηνία δεν μπορείς πλέον να παραγγείλεις ή να αυξήσεις γεύματα από το μενού ημέρας με αυτόν τον τρόπο.'
        : 'You can no longer place or increase set menu meals for this date in this way.';
}

export function noOrderableMealsForDayMessage(userLang: Language): string {
    return userLang === 'el'
        ? 'Για αυτή την ημερομηνία δεν μπορείς πλέον να κάνεις νέα παραγγελία.'
        : 'You can no longer place a new order for this date.';
}

export function deviceClockSkewMessage(userLang: Language): string {
    return userLang === 'el'
        ? 'Η ώρα της συσκευής σου φαίνεται λάθος. Ρύθμισέ την να ενημερώνεται αυτόματα και δοκίμασε ξανά.'
        : 'Your device clock looks wrong. Set it to update automatically and try again.';
}

const ORDER_DEADLINE_USER_MESSAGES: readonly string[] = [
    orderDeadlinePassedMessage('el'),
    orderDeadlinePassedMessage('en'),
    setMenuOrderDeadlineMessage('el'),
    setMenuOrderDeadlineMessage('en'),
    noOrderableMealsForDayMessage('el'),
    noOrderableMealsForDayMessage('en'),
    deviceClockSkewMessage('el'),
    deviceClockSkewMessage('en'),
];

function normalizeCallableMessageForMatch(text: string): string {
    return text.replace(/\s*\[\d+\]\s*$/, '').trim();
}

function callableRejectionMessageText(error: unknown): string {
    if (typeof error !== 'object' || error === null) {
        return '';
    }
    const e = error as { message?: string; details?: unknown };
    const detailsMessage = typeof e.details === 'string' ? e.details.trim() : '';
    const message = typeof e.message === 'string' ? e.message.trim() : '';
    return detailsMessage || message;
}

/** User-facing order deadline rejections from Cloud Functions — expected, not bugs. */
export function isExpectedOrderDeadlineCallableError(error: unknown): boolean {
    if (typeof error !== 'object' || error === null || !('code' in error)) {
        return false;
    }
    const code = String((error as { code?: unknown }).code || '').replace(/^functions\//, '');
    if (code !== 'failed-precondition') {
        return false;
    }
    const text = normalizeCallableMessageForMatch(callableRejectionMessageText(error));
    return ORDER_DEADLINE_USER_MESSAGES.some((msg) => msg === text);
}

/** Firebase Admin JWT rejected — common when the machine clock is far off real time. */
export function isDeviceClockSkewError(error: unknown): boolean {
    const parts: string[] = [];
    const visit = (e: unknown, depth = 0): void => {
        if (e == null || depth > 6) {
            return;
        }
        if (typeof e === 'string') {
            parts.push(e);
            return;
        }
        if (typeof e === 'object') {
            const o = e as Record<string, unknown>;
            if (typeof o.message === 'string') {
                parts.push(o.message);
            }
            if (typeof o.code === 'string') {
                parts.push(o.code);
            }
            visit(o.cause, depth + 1);
        }
    };
    visit(error);
    const blob = parts.join(' ').toLowerCase();
    return blob.includes('invalid_grant')
        || blob.includes('invalid-credential')
        || blob.includes('invalid jwt')
        || blob.includes('iat and exp')
        || blob.includes('not properly synced');
}

export type CheckoutDayBlockCategory = 'general_deadline' | 'set_menu_deadline' | 'no_orderable_meals';

export class OrderDayDeadlineError extends Error {
    constructor(
        public readonly userMessage: string,
        public readonly category: CheckoutDayBlockCategory = 'general_deadline',
    ) {
        super(userMessage);
    }
}

/** Reject new orders that include set-menu / catalog-main meals when the menu deadline has passed. */
export function assertNewOrderMenuContentAllowed(
    orderDate: string,
    draftOrder: Order,
    ctx: OrderDayDeadlineContext,
    userLang: Language,
    at: Date = new Date(),
): void {
    if (canOrderMenuMealsForDay(orderDate, ctx.modelConstants, at)) {
        return;
    }
    const menuLines = getOrderSetMenuMealLinesByLineId(draftOrder, ctx.menuMealsCatalog, ctx.menuMealsForOrderDate);
    if (menuLines.some(({ line }) => (line.quantity ?? 0) > 0)) {
        throw new OrderDayDeadlineError(setMenuOrderDeadlineMessage(userLang), 'set_menu_deadline');
    }
}

/** Non-throwing checkout gate for basket drawer preview (server still authoritative on Pay). */
export function getCheckoutDayAllowedResult(
    orderDate: string,
    draftOrder: Order,
    placedOrder: Order | null | undefined,
    ctx: OrderDayDeadlineContext,
    userLang: Language,
    at: Date = new Date(),
): { ok: true } | { ok: false; message: string; category: CheckoutDayBlockCategory } {
    try {
        assertCheckoutDayAllowed(orderDate, draftOrder, placedOrder, ctx, userLang, at);
        return { ok: true };
    } catch (error) {
        if (error instanceof OrderDayDeadlineError) {
            return { ok: false, message: error.userMessage, category: error.category };
        }
        throw error;
    }
}

/** Authoritative server gate — same rules as the order app UI. */
export function assertCheckoutDayAllowed(
    orderDate: string,
    draftOrder: Order,
    placedOrder: Order | null | undefined,
    ctx: OrderDayDeadlineContext,
    userLang: Language,
    at: Date = new Date(),
): void {
    const isChange = isCommittedOrder(placedOrder);
    if (isChange) {
        if (isCancelOrChangeBlockedForDay(orderDate, ctx.modelConstants, at)) {
            throw new OrderDayDeadlineError(orderDeadlinePassedMessage(userLang));
        }
        if (!validateSetMenuOrderCompletion(orderDate, draftOrder, placedOrder, ctx, at).ok) {
            throw new OrderDayDeadlineError(setMenuOrderDeadlineMessage(userLang), 'set_menu_deadline');
        }
        return;
    }
    // Menu deadline can pass while daily meals / general deadline still allow the day — always scan the draft.
    if (!canOrderMenuMealsForDay(orderDate, ctx.modelConstants, at)) {
        assertNewOrderMenuContentAllowed(orderDate, draftOrder, ctx, userLang, at);
    }
    if (isNewOrderBlockedForDay(orderDate, ctx, at)) {
        if (isOrderDateBeforeAthensToday(orderDate, at) || isGeneralOrderDeadlinePassed(orderDate, ctx.modelConstants, at)) {
            throw new OrderDayDeadlineError(orderDeadlinePassedMessage(userLang));
        }
        if (isSetDayMenuDate(orderDate, ctx.setDayMenuRangesRoot) && !canOrderMenuMealsForDay(orderDate, ctx.modelConstants, at)) {
            throw new OrderDayDeadlineError(setMenuOrderDeadlineMessage(userLang), 'set_menu_deadline');
        }
        throw new OrderDayDeadlineError(noOrderableMealsForDayMessage(userLang), 'no_orderable_meals');
    }
    assertNewOrderMenuContentAllowed(orderDate, draftOrder, ctx, userLang, at);
}

/** Short labels for user messages, e.g. `30/9`, `1/10` (sorted by order date). */
export function formatBasketDayLabelsForMessage(orderDates: string[]): string {
    const sorted = [...orderDates].sort();
    return sorted.map(d => getDayAndMonth(toDateFromServer(d))).join(', ');
}

function joinListForLang(items: string[], lang: Language): string {
    if (items.length <= 1) {
        return items[0] ?? '';
    }
    const conj = lang === 'el' ? ' και ' : ' and ';
    if (items.length === 2) {
        return `${items[0]}${conj}${items[1]}`;
    }
    const last = items[items.length - 1];
    const head = items.slice(0, -1).join(', ');
    return `${head}${conj}${last}`;
}

/** User-facing prompt when basket checkout includes days past the order deadline. */
export function expiredBasketDaysCheckoutMessage(expiredDates: string[], lang: Language): string {
    const labels = formatBasketDayLabelsForMessage(expiredDates).split(', ').filter(Boolean);
    const dayList = joinListForLang(labels, lang);
    if (lang === 'el') {
        return `Έχεις γεύματα για τις ${dayList} για τις οποίες η προθεσμία έχει περάσει. Πρέπει να τις αφαιρέσεις για να συνεχίσεις την παραγγελία. Θέλεις να τις αφαιρέσουμε αυτόματα;`;
    }
    return `You have meals for ${dayList} for which the deadline has passed. You have to remove them to proceed with your order. Would you like us to remove them automatically?`;
}

/** Post-payment basket fulfillment: evaluate deadlines with a short grace for payment latency. */
export function deadlineCheckAtForCheckoutFulfillment(now: Date = new Date()): Date {
    return new Date(now.getTime() - CHECKOUT_FULFILLMENT_DEADLINE_GRACE_MS);
}

/** Paid basket fulfillment — clocks to try before dropping a day (any pass → commit). */
export type CheckoutFulfillmentDeadlineOptions = {
    now?: Date;
    /** {@link OrderCheckoutSession.createdAt} — initiate already validated days at quote time. */
    sessionCreatedAt: number;
};

export function checkoutFulfillmentDeadlineCheckAnchors({ now = new Date(), sessionCreatedAt }: CheckoutFulfillmentDeadlineOptions): Date[] {
    return [deadlineCheckAtForCheckoutFulfillment(now), new Date(sessionCreatedAt)];
}

/** True if the day was committable at fulfillment time or when the checkout session was quoted. */
export function isCheckoutDayAllowedForBasketFulfillment(
    orderDate: string,
    draftOrder: Order,
    placedOrder: Order | null | undefined,
    ctx: OrderDayDeadlineContext,
    userLang: Language,
    options: CheckoutFulfillmentDeadlineOptions,
): boolean {
    for (const at of checkoutFulfillmentDeadlineCheckAnchors(options)) {
        try {
            assertCheckoutDayAllowed(orderDate, draftOrder, placedOrder, ctx, userLang, at);
            return true;
        } catch (e) {
            if (!(e instanceof OrderDayDeadlineError)) {
                throw e;
            }
        }
    }
    return false;
}

export function assertCheckoutDayAllowedForBasketFulfillment(
    orderDate: string,
    draftOrder: Order,
    placedOrder: Order | null | undefined,
    ctx: OrderDayDeadlineContext,
    userLang: Language,
    options: CheckoutFulfillmentDeadlineOptions,
): void {
    let lastDeadlineError: OrderDayDeadlineError | undefined;
    for (const at of checkoutFulfillmentDeadlineCheckAnchors(options)) {
        try {
            assertCheckoutDayAllowed(orderDate, draftOrder, placedOrder, ctx, userLang, at);
            return;
        } catch (e) {
            if (e instanceof OrderDayDeadlineError) {
                lastDeadlineError = e;
                continue;
            }
            throw e;
        }
    }
    throw lastDeadlineError ?? new OrderDayDeadlineError(orderDeadlinePassedMessage(userLang));
}

/** Paid basket fulfillment — drop days that are no longer committable (deadline passed on server). */
export function shouldDropBasketDayAtFulfill(
    orderDate: string,
    draftOrder: Order,
    placedOrder: Order | null | undefined,
    ctx: OrderDayDeadlineContext,
    sessionCreatedAt: number,
    now: Date = new Date(),
): boolean {
    return !isCheckoutDayAllowedForBasketFulfillment(orderDate, draftOrder, placedOrder, ctx, 'en', { now, sessionCreatedAt });
}
